import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useForm, useFormState } from "react-hook-form";
import { useNavigate } from "react-router";

import { useApi } from "@/api/hooks/useApi";
import { useLogout } from "@/authentication/useLogout";
import { AuthenticationPage } from "@/components/AuthenticationPage/AuthenticationPage";
import { Form } from "@/components/Form/Form";
import { Button } from "@/design-system/Button";
import { CodeInput } from "@/design-system/CodeInput";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Stack } from "@/design-system/Stack";
import { isHttpError } from "@/helpers/Network/errors";
import { useBool } from "@/hooks/useBool";
import { useQueryParam } from "@/hooks/useQueryParam";
import { QUERY_KEYS } from "@/query-keys";
import { Trans, useTranslation } from "@/translations";

const locationInputOptions = { app: "navigationLinkApp", email: "navigationLinkEmail" } as const;

let initialConnect = false;

export function RegistrationPage(): React.ReactNode {
  const logout = useLogout();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const api = useApi();
  const [isLoading, loadingHandlers] = useBool(true);
  const [isSubmitting, submittingHandlers] = useBool(false);
  const [error, setError] = useState<unknown>();
  const [validationError, setValidationError] = useState<"unknownLocation" | "locationUserLimitReached" | undefined>();
  const [queryParamCode] = useQueryParam("code");
  const [queryParamLocationCodeInput] = useQueryParam("type");
  const form = useForm<{ code: string }>({
    resolver: (data) => {
      if (!data.code || data.code.length !== 8) {
        return {
          values: data as any,
          errors: {
            code: {
              type: "required",
              message: t("page.registration.errors.code-required"),
            },
          },
        };
      }

      return {
        values: data,
        errors: {} as Record<keyof typeof data, never>,
      };
    },
  });
  const {
    errors: { code: codeError },
  } = useFormState(form);

  const handleRegister = useCallback(
    async function register(code: string) {
      submittingHandlers.setTrue();

      try {
        const result = await api.postRegistrationConnectV2({
          languageId: i18n.language || undefined,
          code,
          locationCodeInput: "manual",
        });

        if (result.data.result === "success") {
          void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SELF_PROJECTS() });
          // SessionUserLoader will navigate to onboarding flow
          void navigate("/");
        } else if (result.data.result === "locationUserLimitReached") {
          setValidationError(result.data.result);
        } else if (result.data.result === "unknownLocation") {
          setValidationError(result.data.result);
        }
      } catch (registrationError) {
        setError(registrationError);
      } finally {
        submittingHandlers.setFalse();
      }
    },
    [submittingHandlers, api, i18n.language, queryClient, navigate],
  );

  useEffect(() => {
    async function execute() {
      if (initialConnect) {
        return;
      }

      initialConnect = true;

      try {
        const result = await api.postRegistrationConnectV2({
          languageId: i18n.language || undefined,
          code: queryParamCode || undefined,
          locationCodeInput: queryParamCode
            ? (queryParamLocationCodeInput
                ? locationInputOptions[queryParamLocationCodeInput as keyof typeof locationInputOptions]
                : undefined) || "unknown"
            : "noInput",
        });

        if (result.data.result === "success") {
          void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SELF_PROJECTS() });
          // SessionUserLoader will navigate to onboarding flow
          void navigate("/");
        } else if (result.data.result === "locationUserLimitReached") {
          setValidationError(result.data.result);
        }
        // If we have an unknown location we shouldn't show an error
      } catch (registrationError) {
        setError(registrationError);
      } finally {
        loadingHandlers.setFalse();
      }
    }

    // The visible registration page synchronizes its one-time link code with the registration API.
    void execute();
  }, [api, queryParamCode, i18n.language, loadingHandlers, navigate, queryParamLocationCodeInput, queryClient]);

  if (isLoading) {
    // no delay to prevent flickering
    return <FullSizeLoader withPadding noDelay />;
  }

  function getErrorMessage() {
    if (codeError) {
      return codeError.message;
    }
    if (validationError === "locationUserLimitReached") {
      return t("page.registration.errors.too-many-users");
    }
    if (validationError === "unknownLocation") {
      return t("page.registration.errors.unknown-location");
    }
    if (isHttpError(error) && error.status === 429) {
      return t("page.registration.errors.too-many-attempts");
    }

    return t("page.registration.errors.unknown");
  }

  return (
    <AuthenticationPage
      header={isSubmitting ? t("page.registration.title.validating") : t("page.registration.title")}
      body={isSubmitting ? undefined : t("page.registration.subtitle")}
    >
      <Form.Root<{ code: string }>
        formMethods={form}
        onSubmit={(d) => {
          if (isSubmitting) {
            return;
          }

          void handleRegister(d.code);
        }}
        noPrompt
      >
        {isSubmitting ? (
          <section className="py-page-bottom">
            <FullSizeLoader withPadding />
          </section>
        ) : (
          <Stack gap="md">
            <Stack gap="lg" align="center">
              <section className="mt-space-md flex flex-col gap-0.5 text-center">
                {error || validationError || codeError ? (
                  <p className="mb-space-md text-center text-caption text-text-danger-tertiary">{getErrorMessage()}</p>
                ) : null}
                <label className="mb-space-md text-body-emphasis" htmlFor="email">
                  {t("page.registration.form.title")}
                </label>
                <CodeInput
                  aria-label={t("page.registration.form.title")}
                  autoFocus
                  length={8}
                  onChange={(code) => form.setValue("code", code)}
                />
              </section>
              <Button type="submit" fullWidth>
                {t("page.registration.form.submit")}
              </Button>
            </Stack>
            <section className="text-left text-caption">
              <h2 className="text-body-emphasis">{t("page.registration.help.title")}</h2>
              <ul className="my-space-md list-disc pl-space-md">
                {t("page.registration.help.body")
                  .split("\n")
                  .map((item, i) => item && <li key={i}>{item}</li>)}
              </ul>
              <p>
                <Trans
                  i18nKey="page.registration.help.footer"
                  components={{
                    a: (content) => (
                      <Button
                        variant="ghostPrimary"
                        type="link"
                        href="https://areaofpeople.com/veelgestelde-vragen"
                        isExternal
                      >
                        {content}
                      </Button>
                    ),
                  }}
                />
              </p>
              <footer className="mt-space-sm flex justify-center">
                <Button variant="ghostSecondary" size="sm" onClick={() => logout()}>
                  {t("page.registration.help.log-out")}
                </Button>
              </footer>
            </section>
          </Stack>
        )}
      </Form.Root>
    </AuthenticationPage>
  );
}
